import type {
	GetSearchResultsQuery,
	GetSearchResultsQueryVariables,
} from '@/features/search/graphql/queries/GetSearchResults.query';

import { GetSearchResultsDocument } from '@/features/search/graphql/queries/GetSearchResults.query';

import { CollectionType } from '@/enums/collection-type';

import { captureMessageForSentry } from '@/libraries/sentry';
import { toastError } from '@/helpers/vfm/toast-helper';

import { isNavigationFailure, NavigationFailureType } from 'vue-router';

import { SnowplowPersonContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers';
import { useSearchTracking } from '@/features/search/tracking';
import { buildUrlForDomain, Domain } from '@/routing/helpers/subdomain-helper';
import { routeConfig } from '@/routing/config';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useFilterStore } from '@/stores/pinia/useFilterStore';

/** Minimum length of search term to start query. */
const MIN_QUERY_LENGTH = 2;

const query = ref('');
const personId = ref<number | null>(null);
const isShown = ref(false);

export function useSearch() {
	const route = useRoute();
	const router = useRouter();

	const userStore = useUserStore();
	const routingStore = useRoutingStore();
	const languageStore = useLanguageStore();
	const filterStore = useFilterStore();

	const { trackSearch, updateSearchSession } = useSearchTracking();

	const isSearchSuggesterOpen = computed(() => import.meta.client && isShown.value);

	/**
	 * @param visible whether to set suggester visible or not.
	 * @param [track=true] set false to not track close/open event.
	 */
	function setSearchSuggesterVisibility(visible: boolean, track = true) {
		if (isShown.value === visible) return;
		isShown.value = visible;

		if (visible === true) {
			// when you open the search bar while on the search page, "resume the session"
			const paramSearchQuery = route.query.q?.toString();
			if (route.name === 'app.search.list' && paramSearchQuery) {
				query.value = paramSearchQuery;
			}
		}
		else {
			query.value = '';
		}

		// old skool influenced tracking
		const event = visible ? 'openSearch' : 'closeSearch';

		if (track) trackSearch(event);
	}

	function search(searchQuery: string) {
		query.value = searchQuery ?? '';
		updateSearchSession(searchQuery);
	}

	/**
	 * Routing to search page along with setting filters.
	 *
	 * @param searchInfo Search term and maybe person ID
	 * @param searchInfo.searchString Actual search term
	 * @param [searchInfo.personId] Optional person ID
	 * @param [track=true] Whether to track the suggester open/close
	 */
	function goToSearch({ searchString, personId }: { searchString: string; personId?: number }, track = true) {
		const routeQuery: { q: string; person_id?: string } = { q: searchString };
		if (personId != null) routeQuery.person_id = personId.toString();

		userStore.saveSearchTerm(searchString);

		// Check if we're on a subdomain and need to redirect to main domain for search
		if (routingStore.activeDomain !== Domain.MAIN) {
			// We're on a subdomain, need to redirect to main domain for search
			const webLocale = languageStore.webLocale;

			// Build search URL with query params using localized search path
			const searchParams = new URLSearchParams();
			searchParams.set('q', searchString);
			if (personId) {
				searchParams.set('person_id', personId.toString());
			}

			// Get the localized search path (e.g., 'Suche' for German, 'recherche' for French)
			const searchConfig = routeConfig['app.search.list'];
			const localizedSearchPath = searchConfig.urls[webLocale as keyof typeof searchConfig.urls] || 'search';

			const searchPath = `/${webLocale}/${localizedSearchPath}?${searchParams.toString()}`;

			if (!import.meta.client) return;
			const targetUrl = buildUrlForDomain(searchPath, Domain.MAIN);

			// Close search suggester and redirect
			setSearchSuggesterVisibility(false, track);
			window.location.href = targetUrl;
			return;
		}

		// We're on the main domain or on server, proceed with normal router navigation
		const newRoute = filterStore.updateRouterState({ collectionType: CollectionType.SEARCH, routeQuery });
		if (newRoute) {
			router.push(newRoute).catch((error: any) => {
				if (
					!isNavigationFailure(error, NavigationFailureType.duplicated) &&
					!isNavigationFailure(error, NavigationFailureType.cancelled)
					// Search.vue component generates a navigation canceled due to
					// a reset of the filters which sometimes triggers a navigation
				) {
					throw error;
				}
			});
		}

		setSearchSuggesterVisibility(false, track);
	}

	/* SEARCH RESULTS QUERY */
	const enabled = computed(() => query.value.length > MIN_QUERY_LENGTH);

	const { t } = useI18n();

	const {
		result: searchResults,
		onError,
		loading,
	} = useQuery<GetSearchResultsQuery, GetSearchResultsQueryVariables>(
		GetSearchResultsDocument,
		() => ({
			country: languageStore.country,
			language: languageStore.language,
			searchQuery: query.value,
			first: 4,
			location: 'SearchSuggester',
		}),
		{ enabled }
	);

	const isLoading = computed(() => enabled.value && loading.value);

	const searchTitles = computed(() => searchResults.value?.searchTitles.edges?.map(title => title.node) || []);

	const searchPeople = computed(() => searchResults.value?.popularPeople.edges?.map(person => person.node) || []);

	const hasSearchResults = computed(() => isSearchSuggesterOpen.value && enabled.value);

	const isSearchResultsEmpty = computed(
		() => !loading.value && enabled.value && searchTitles.value.length === 0 && searchPeople.value.length === 0
	);

	const searchTitleContexts = computed(() =>
		searchTitles.value.map(
			(title, index) => new SnowplowTitleContextGraphql(title.objectId, title.objectType, null, null, null, index)
		)
	);

	const searchPersonContexts = computed(() =>
		searchPeople.value.map(person => new SnowplowPersonContext(person.objectId, person.content.fullName))
	);

	onError(error => {
		captureMessageForSentry(
			'[GraphQL Get Search Results]:',
			{ error, where: 'Component: SearchSuggesterResults' },
			'error'
		);

		toastError({ message: t('WEBAPP_ALERT_ERROR_TITLE'), duration: 2000 });
	});

	return {
		isSearchSuggesterOpen,
		setSearchSuggesterVisibility,

		personId,

		query: readonly(query),
		search,

		goToSearch,

		/* SEARCH RESULTS */
		searchTitles,
		searchPeople,
		hasSearchResults,
		isSearchResultsEmpty,
		isLoading,

		searchTitleContexts,
		searchPersonContexts,
	};
}
